/**
 * adminAccess — secret-hash gate for the /admin routes.
 *
 * Admin pages are only reachable when the URL carries the unlock fragment, e.g.
 *   /admin#dukk
 * Without it the route behaves as if it does not exist (SuperUserGate sends the
 * visitor to "/"). Once unlocked, the flag lives in sessionStorage so links
 * *inside* the dashboard (/admin/shops, /admin/users, …) keep working for the
 * rest of the tab's lifetime, and the fragment is stripped from the address bar.
 *
 * NOTE: this is obscurity, not authorization. The fragment never leaves the
 * browser (it is not sent to the server) and the route table ships in the public
 * JS bundle, so anyone reading the bundle can find it. The real protections are
 * SuperUserGate (role check) and, decisively, `Depends(get_superuser)` on the
 * /api/admin/* endpoints — those stay in place and are what actually keeps data
 * safe. Treat this purely as "keep it out of sight of casual visitors".
 */

const STORAGE_KEY = 'dukkan_admin_unlocked'

/** Fragment required to reveal the admin routes, without the leading '#'. */
export const ADMIN_UNLOCK_HASH: string =
  ((import.meta as any).env?.VITE_ADMIN_UNLOCK_HASH as string | undefined) || 'dukk'

/**
 * True when this tab may render admin routes.
 * Consumes `#<secret>` on first sight, then remembers it for the session.
 */
export function isAdminUnlocked(): boolean {
  try {
    if (sessionStorage.getItem(STORAGE_KEY) === '1') return true

    const hash = window.location.hash.replace(/^#/, '')
    if (hash && hash === ADMIN_UNLOCK_HASH) {
      sessionStorage.setItem(STORAGE_KEY, '1')
      // Drop the fragment so the secret is not left sitting in the address bar
      // (or copied into a shared link / screenshot).
      window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search,
      )
      return true
    }
  } catch {
    // sessionStorage unavailable (private mode / blocked) — fall through to the
    // hash-only check below so the gate is still usable.
    return window.location.hash.replace(/^#/, '') === ADMIN_UNLOCK_HASH
  }
  return false
}

/** Forget the unlock — admin routes hide again until the fragment is re-entered. */
export function lockAdmin(): void {
  try { sessionStorage.removeItem(STORAGE_KEY) } catch { /* ignore */ }
}
